// Puzzle order and randomization validation utilities
// Verifies shuffle quality, no alphabetical segments, no-repeat enforcement

import { getPuzzleSequence } from './wordlistEngine';

export interface ShuffleQualityReport {
  uniformityScore: number; // 0-100, higher is better
  firstLetterClusters: Record<string, number>;
  maxClusterSize: number;
  passed: boolean;
  warnings: string[];
}

export interface Segment {
  start: number;
  end: number;
  words: string[];
}

// Check shuffle uniformity - ensures no letter clustering
export function checkShuffleUniformity(words: string[]): ShuffleQualityReport {
  const warnings: string[] = [];
  
  // Count first letter distribution
  const firstLetterClusters: Record<string, number> = {};
  words.forEach(word => {
    const firstLetter = word[0].toUpperCase();
    firstLetterClusters[firstLetter] = (firstLetterClusters[firstLetter] || 0) + 1;
  });
  
  // Find max cluster size in consecutive positions
  let maxClusterSize = 1;
  let currentCluster = 1;
  
  for (let i = 1; i < words.length; i++) {
    if (words[i][0].toUpperCase() === words[i - 1][0].toUpperCase()) {
      currentCluster++;
      maxClusterSize = Math.max(maxClusterSize, currentCluster);
    } else {
      currentCluster = 1;
    }
  }
  
  // Calculate position-based uniformity score
  // For each letter, check if words starting with that letter are evenly distributed
  // across positions in the shuffled list (not clustered together)
  const letterPositions: Record<string, number[]> = {};
  words.forEach((word, index) => {
    const firstLetter = word[0].toUpperCase();
    if (!letterPositions[firstLetter]) {
      letterPositions[firstLetter] = [];
    }
    letterPositions[firstLetter].push(index);
  });
  
  // Calculate average gap between positions for each letter
  let totalUniformity = 0;
  let letterCount = 0;
  
  Object.entries(letterPositions).forEach(([letter, positions]) => {
    if (positions.length < 2) {
      // Single word can't be clustered
      totalUniformity += 100;
      letterCount++;
      return;
    }
    
    // Calculate gaps between consecutive positions
    const gaps: number[] = [];
    for (let i = 1; i < positions.length; i++) {
      gaps.push(positions[i] - positions[i - 1]);
    }
    
    // Expected average gap if perfectly distributed
    const expectedGap = words.length / positions.length;
    
    // Calculate variance of gaps from expected
    const gapVariances = gaps.map(gap => Math.abs(gap - expectedGap) / expectedGap);
    const avgVariance = gapVariances.reduce((sum, v) => sum + v, 0) / gapVariances.length;
    
    // Convert to 0-100 score where 0 variance = 100 score
    // High variance (>1) = clustered = low score
    const letterScore = Math.max(0, Math.min(100, 100 - (avgVariance * 50)));
    totalUniformity += letterScore;
    letterCount++;
  });
  
  const uniformityScore = letterCount > 0 ? totalUniformity / letterCount : 0;
  
  if (maxClusterSize > 3) {
    warnings.push(`Found consecutive cluster of ${maxClusterSize} words starting with same letter`);
  }
  
  if (uniformityScore < 60) {
    warnings.push(`Shuffle uniformity score is low: ${uniformityScore.toFixed(1)}/100`);
  }
  
  return {
    uniformityScore,
    firstLetterClusters,
    maxClusterSize,
    passed: warnings.length === 0,
    warnings
  };
}

// Detect alphabetical segments in shuffled order
export function detectAlphabeticalSegments(words: string[]): Segment[] {
  const segments: Segment[] = [];
  let currentSegment: string[] = [words[0]];
  let startIndex = 0;
  
  for (let i = 1; i < words.length; i++) {
    if (words[i] > words[i - 1]) {
      currentSegment.push(words[i]);
    } else {
      if (currentSegment.length >= 8) {
        segments.push({
          start: startIndex,
          end: i - 1,
          words: [...currentSegment]
        });
      }
      currentSegment = [words[i]];
      startIndex = i;
    }
  }
  
  // Check final segment
  if (currentSegment.length >= 8) {
    segments.push({
      start: startIndex,
      end: words.length - 1,
      words: currentSegment
    });
  }
  
  return segments;
}

// Verify no future puzzle leaks
export function verifyNoFutureLeaks(currentDate: Date): boolean {
  // Check that puzzle selection doesn't accidentally expose future words
  // This is a placeholder - actual implementation would check browser storage
  return true;
}

// Verify no-repeat enforcement
export function verifyNoRepeats(words: string[]): boolean {
  const uniqueSet = new Set(words);
  return uniqueSet.size === words.length;
}

// Get sample puzzle sequence for analysis
export function analyzePuzzleSequence(startDate: Date, count: number = 30): {
  sequence: Array<{ date: string; word: string; index: number }>;
  hasAlphabeticalPattern: boolean;
  hasDuplicates: boolean;
  uniformityScore: number;
} {
  const sequence = getPuzzleSequence(startDate, count);
  const words = sequence.map(p => p.word);
  
  const alphabeticalSegments = detectAlphabeticalSegments(words);
  const hasAlphabeticalPattern = alphabeticalSegments.length > 0;
  
  const uniqueWords = new Set(words);
  const hasDuplicates = uniqueWords.size !== words.length;
  
  const uniformity = checkShuffleUniformity(words);
  
  return {
    sequence,
    hasAlphabeticalPattern,
    hasDuplicates,
    uniformityScore: uniformity.uniformityScore
  };
}
